import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

// Module-level store so non-React code (lib/seo.js) can read overrides synchronously.
let STORE = {};
export const getContent = (key) => STORE[key];

const ContentContext = createContext({ get: (_k, d) => d, isOwner: false, editMode: false });

export function ContentProvider({ children }) {
  const { user } = useAuth();
  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [editMode, setEditModeState] = useState(() => localStorage.getItem("urban_edit_mode") === "1");
  const isOwner = user?.role === "owner";

  useEffect(() => {
    api.get("/cms/content").then(({ data }) => { STORE = data || {}; setContent(STORE); }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const setEditMode = useCallback((v) => {
    setEditModeState(v);
    localStorage.setItem("urban_edit_mode", v ? "1" : "0");
  }, []);

  const get = useCallback((key, fallback) => {
    const v = content[key];
    return v === undefined || v === null || v === "" ? fallback : v;
  }, [content]);

  const save = useCallback(async (key, value, meta = {}) => {
    const { data } = await api.put(`/cms/content/${key}`, { value, type: meta.type || "text", page: meta.page || window.location.pathname, label: meta.label || key });
    STORE = { ...STORE, [key]: data.value };
    setContent(STORE);
    return data;
  }, []);

  const applyRestored = useCallback((key, value) => {
    STORE = { ...STORE };
    if (value === undefined || value === null) delete STORE[key]; else STORE[key] = value;
    setContent(STORE);
  }, []);

  const value = useMemo(() => ({ get, save, applyRestored, content, loaded, isOwner, editMode: isOwner && editMode, setEditMode }),
    [get, save, applyRestored, content, loaded, isOwner, editMode, setEditMode]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export const useContent = () => useContext(ContentContext);
